import { BodyText } from "$components/DesignSystem"
import type { ForgotPasswordMutation } from "$relay/ForgotPasswordMutation.graphql"
import { useState } from "react"
import { View, NativeSyntheticEvent, TextInputKeyPressEventData } from "react-native"
import { useRelayEnvironment } from "react-relay"
import { commitMutation, graphql } from "relay-runtime"

import { BASE_MARGIN, ModalForm, PuzmoButton } from "../DesignSystem"
import type { AuthModes } from "./AuthModal"

export function ForgotPassword(props: { updateViewMode: (mode: AuthModes) => void }) {
  const { updateViewMode } = props

  const [email, setEmail] = useState("")
  const [loading, setLoading] = useState(false)
  const [username, setUsername] = useState("")
  const [sent, setSent] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const errored = !!errorMessage
  const env = useRelayEnvironment()

  const onSubmitForgotPassword = async () => {
    setErrorMessage("")
    setLoading(true)

    try {
      commitMutation<ForgotPasswordMutation>(env, {
        mutation: Mutation,
        variables: { email, username },
        onCompleted: (response) => {
          setLoading(false)
          if (response.requestLostPasswordEmail) {
            setSent(true)
          } else {
            if (email) {
              setErrorMessage("Error sending email, perhaps we don't have that email address?")
            } else {
              setErrorMessage("Error sending email, perhaps we don't have that username?")
            }
          }
        },
      })
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      setLoading(false)
      setErrorMessage("Error sending email, perhaps the server is having issues?")
    }
  }

  const onEnterSubmit = (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    if (e.nativeEvent.key === "Enter" && (username || email)) {
      onSubmitForgotPassword()
    }
  }

  return (
    <View>
      <ModalForm.Title>Lost details</ModalForm.Title>

      <BodyText style={{ color: "white" }}>
        Tell us your email or username#id associated with your Puzzmo account, and we'll email you a link to change your password.
      </BodyText>

      <View style={{ marginTop: 20 }}>
        <ModalForm.Subtitle>Email</ModalForm.Subtitle>
        <ModalForm.TextInput
          key="email"
          value={email}
          onChangeText={setEmail}
          keyboardType="email-address"
          onKeyPress={onEnterSubmit}
          error={errored && email.length > 0}
          loading={loading}
          editable={!loading}
        />

        <ModalForm.Subtitle>Or username</ModalForm.Subtitle>
        <ModalForm.TextInput
          key="username"
          value={username}
          onChangeText={setUsername}
          keyboardType="ascii-capable"
          onKeyPress={onEnterSubmit}
          error={errored && username.length > 0}
          loading={loading}
          editable={!loading}
          placeholder="e.g. puzzmo#tk"
        />
      </View>

      {errorMessage && <ModalForm.ErrorMessage>{errorMessage}</ModalForm.ErrorMessage>}

      <PuzmoButton
        block
        title={loading ? "Requesting" : sent ? " Sent" : "Request an email"}
        bgKey="a_puzmo"
        color="alwaysDark"
        onPress={onSubmitForgotPassword}
        disabled={(username?.length === 0 && email?.length === 0) || loading || sent}
        style={{ marginTop: BASE_MARGIN }}
      />
    </View>
  )
}

const Mutation = graphql`
  mutation ForgotPasswordMutation($email: String, $username: String) {
    requestLostPasswordEmail(email: $email, username: $username)
  }
`
